import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { ComponentPropsWithoutRef, useEffect } from 'react'
import TextareaWithLabel from '../textarea'
import type { AdditionalNotesType } from '@/types/data'
import { useInvestigationStore } from '@/lib/stores/investigation'
import { useCurrentInvestigation } from '@/hooks/useCurrentInvestigation'
import { cn, withFutureDateNote } from '@/lib/utils'

export function AdditionalNotesEntries({
  values,
  onChange,
  disabled,
  className,
  entries = ['pharmacist', 'customerService'],
  ...props
}: {
  disabled?: boolean
  values: AdditionalNotesType
  entries?: (keyof AdditionalNotesType)[]
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>, entry: keyof AdditionalNotesType) => void
} & Omit<ComponentPropsWithoutRef<'div'>, 'onChange'>) {
  return (
    <div className={cn('flex flex-col gap-5', className)} {...props}>
      {entries.map(entry => {
        let formattedTitle = ''
        if (entry === 'pharmacist') {
          formattedTitle = 'Pharmacy'
        } else if (entry === 'customerService') {
          formattedTitle = 'Customer Service'
        } else if (entry === 'afterCare') {
          formattedTitle = 'After Care'
        } else if (entry === 'clinician') {
          formattedTitle = 'Clinicians'
        }

        return (
          <div data-testid={entry} key={entry}>
            <span className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] mb-6'>
              {formattedTitle} notes{' '}
              <span className='text-custom-neutral-600  font-normal leading-[170%]'>(Optional)</span>
            </span>
            <p className='text-[#1D1D1D] font-medium leading-[170%] mb-4'>Add Notes</p>
            <TextareaWithLabel
              isDisabled={disabled}
              title={`${formattedTitle} notes`}
              value={values?.[entry] ?? ''}
              onChange={e => onChange(e, entry)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default function AdditionalNotes({
  isEditable,
  patientOrigin = 'medication-request'
}: {
  isEditable: boolean
  patientOrigin?: 'medication-request' | 'investigation'
}) {
  const { notes: medReqNotes, setNotes: setMedReqNotes, medicationIssueDate } = useMedicationRequestStore()
  const { notes: investigationNotes, setNotes: setInvestigationNotes } = useInvestigationStore()

  const isMedReq = patientOrigin === 'medication-request'
  const notes = isMedReq ? medReqNotes : investigationNotes
  const setNotes = isMedReq ? setMedReqNotes : setInvestigationNotes

  const investigation = useCurrentInvestigation(patientOrigin === 'investigation')
  const medRequest = useCurrentMedication(
    !!(patientOrigin === 'medication-request' || investigation?.data?.medicationRequestId),
    investigation?.data?.medicationRequestId,
    isMedReq
  )

  useEffect(() => {
    const oldNotes = isMedReq ? useMedicationRequestStore.getState().notes : useInvestigationStore.getState().notes

    if (isMedReq) {
      oldNotes.pharmacist = withFutureDateNote(oldNotes.pharmacist, medicationIssueDate)
    }

    setNotes(oldNotes)
  }, [medicationIssueDate])

  useEffect(() => {
    if (!medRequest?.data) return

    const { data } = medRequest
    setNotes({
      pharmacist: data.additionalNotes?.pharmacist ?? '',
      customerService: data.additionalNotes?.customerService ?? '',
      afterCare: data.additionalNotes?.afterCare ?? '',
      clinician: data.additionalNotes?.clinician ?? ''
    })
  }, [medRequest?.data?.id])

  const noteEntries: (keyof AdditionalNotesType)[] = isMedReq
    ? ['pharmacist', 'customerService', 'afterCare', 'clinician']
    : ['pharmacist', 'customerService']

  return (
    <AdditionalNotesEntries
      id='add-notes'
      values={notes}
      entries={noteEntries}
      disabled={!isEditable}
      onChange={(e, entry) => {
        const { notes: newNotes } = isMedReq ? useMedicationRequestStore.getState() : useInvestigationStore.getState()
        setNotes({
          ...newNotes,
          [entry]: e.target.value
        })
      }}
    />
  )
}
